// Formattazione e calcolo periodi della sezione Finanza (mesi 'YYYY-MM', come le fotografie del server)
import { MONTHS } from '@/components/dashboard/format';

export { formatEurCents, formatNumber, formatPct, formatDelta, deltaColor, platformColor } from '@/components/dashboard/format';

const pad = n => String(n).padStart(2, '0');

export const VAT_RATES = [22, 10, 5, 4, 0];

export const VAT_NATURES = [
	{ value: 'esente', text: 'Esente' },
	{ value: 'non_imponibile', text: 'Non imponibile' },
	{ value: 'fuori_campo', text: 'Fuori campo IVA' },
	{ value: 'reverse_charge', text: 'Reverse charge' },
	{ value: 'nd', text: 'Non indicata' },
];

// etichetta del box IVA: '22' -> 'IVA 22%', '0:esente' -> '0% esente'
export const vatLabel = key => {
	if (!key) return '';
	if (!String(key).startsWith('0:')) return `IVA ${key}%`;
	const nature = String(key).split(':')[1];
	if (nature === 'reverse_charge') return 'Reverse charge';
	if (nature === 'oss') return 'OSS: IVA di altri Paesi UE';
	if (nature === 'nd') return '0% / non indicata';
	return `0% ${(VAT_NATURES.find(n => n.value === nature) || { text: nature }).text.toLowerCase()}`;
};

export const SOURCE_LABELS = { xml: 'Acquisti XML', fic: 'FattureInCloud', manuali: 'Costi manuali', commissioni: 'Commissioni' };
export const sourceLabel = key => SOURCE_LABELS[key] || key;

export const monthKey = (date = new Date()) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;

export const addMonths = (month, n) => {
	const [y, m] = month.split('-').map(Number);
	const index = y * 12 + (m - 1) + n;
	return `${Math.floor(index / 12)}-${pad((index % 12) + 1)}`;
};

export const previousMonth = () => addMonths(monthKey(), -1);

export const monthLabel = month => {
	if (!month) return '';
	const [y, m] = month.split('-');
	return `${MONTHS[+m - 1].text} ${y}`;
};

/**
 * Periodo scelto nella barra -> intervallo di mesi [from, to] da chiedere al server.
 * I mesi futuri non si chiedono: l'anno in corso si ferma al mese in corso.
 */
export const periodMonths = ({ period, year, month, quarter }) => {
	const current = monthKey();
	let from;
	let to;
	if (period === 'year') {
		from = `${year}-01`;
		to = `${year}-12`;
	} else if (period === 'quarter') {
		from = `${year}-${pad((quarter - 1) * 3 + 1)}`;
		to = addMonths(from, 2);
	} else {
		from = `${year}-${pad(month)}`;
		to = from;
	}
	if (to > current) to = current;
	if (from > current) from = current;
	return { from, to };
};

export const STATUS = {
	closed: { label: 'Storico', color: 'primary', icon: 'mdi-lock-outline' },
	provisional: { label: 'Provvisorio', color: 'orange darken-2', icon: 'mdi-progress-clock' },
	open: { label: 'In corso', color: 'grey darken-1', icon: 'mdi-timer-sand' },
};

// nome leggibile di canali e metodi di pagamento (chiavi come arrivano dagli ordini)
const SOURCE_NAMES = {
	amazon: 'Amazon',
	ebay: 'eBay',
	shopify: 'Shopify',
	store: 'Sito',
	cassa: 'Cassa',
	stripe: 'Stripe',
	paypal: 'PayPal',
	paypalcheckout: 'PayPal Checkout',
	googlepay: 'Google Pay',
	klarna: 'Klarna',
	amazonpay: 'Amazon Pay',
	scalapay: 'Scalapay',
	satispay: 'Satispay',
	nexi: 'Nexi',
	postepay: 'PostePay',
	cashdelivery: 'Contrassegno',
	banktransfer: 'Bonifico',
	bonifico: 'Bonifico',
	cash: 'Contanti',
	invoice: 'Fattura',
	crypto: 'Crypto',
};
export const sourceName = key => SOURCE_NAMES[String(key || '').toLowerCase()] || key;

// da dove arriva la commissione di una fonte nel mese
export const FEE_ORIGIN = {
	api: { label: 'API automatica', short: 'API', color: 'green darken-1' },
	manual: { label: 'Totale manuale', short: 'manuale', color: 'primary' },
	estimate: { label: 'Tariffa', short: 'tariffa', color: 'orange darken-2' },
	mixed: { label: 'Fonti diverse nei mesi', short: 'misto', color: 'grey darken-1' },
	marketplace: { label: 'Nella commissione del canale', short: 'nel canale', color: 'grey darken-1' },
	included: { label: 'Inclusa in Stripe', short: 'in Stripe', color: 'grey darken-1' },
	nofee: { label: 'Senza commissione', short: 'nessuna', color: 'grey darken-1' },
	none: { label: 'Non indicata', short: 'non indicata', color: 'grey' },
};

const eur = value => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(+value || 0);
const pct = value => `${String(+value || 0).replace('.', ',')}%`;

// "1% + 0,50 €", "gratis"
const tierText = t => {
	const parts = [];
	if (+t.percent) parts.push(pct(t.percent));
	if (+t.fixed) parts.push(eur(t.fixed));
	return parts.length ? parts.join(' + ') : 'gratis';
};

/**
 * Riassunto di una versione del listino: "fino a 10,00 €: gratis · oltre: 0,5% + 0,20 € · minimo 0,80 € · canone 14,90 €/mese"
 */
export const rateSummary = rate => {
	if (!rate) return '';
	const tiers = rate.tiers && rate.tiers.length ? rate.tiers : [{ upTo: null, percent: rate.percent, fixed: rate.fixed }];
	const text =
		tiers.length === 1
			? `${tierText(tiers[0])} per ordine`
			: tiers.map((t, i) => (t.upTo === null || t.upTo === undefined || t.upTo === '' ? `${i ? 'oltre' : 'sempre'}: ${tierText(t)}` : `fino a ${eur(t.upTo)}: ${tierText(t)}`)).join(' · ');
	const extra = [];
	if (+rate.minPerOrder) extra.push(`minimo ${eur(rate.minPerOrder)}`);
	if (+rate.maxPerOrder) extra.push(`massimo ${eur(rate.maxPerOrder)}`);
	if (+rate.monthlyFee) extra.push(`canone ${eur(rate.monthlyFee)}/mese`);
	return [text, ...extra].join(' · ');
};
